import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children }) => (
  <span className="font-mono2 text-[11px] uppercase tracking-[0.32em] text-red-500" data-testid="section-eyebrow">
    {children}
  </span>
);

export const SectionHead = ({ eyebrow, title, sub, align = "left" }) => (
  <Reveal className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
      {title}
    </h2>
    {sub && <p className="mt-5 text-sm leading-relaxed text-zinc-400 sm:text-base">{sub}</p>}
  </Reveal>
);

export const CTAButton = ({
  href,
  onClick,
  children,
  variant = "primary",
  testid,
  className = "",
  type = "button",
}) => {
  const base =
    "group relative inline-flex items-center justify-center gap-2 overflow-hidden px-7 py-3.5 font-display text-[13px] font-extrabold uppercase tracking-[0.14em] transition-all duration-300";
  const styles = {
    primary:
      "bg-gradient-to-r from-red-600 via-rose-600 to-red-700 text-white shadow-[0_0_24px_rgba(220,38,38,0.35)] hover:shadow-[0_0_38px_rgba(220,38,38,0.55)] hover:brightness-110",
    ghost: "border border-white/25 text-white hover:border-red-500 hover:text-red-400",
    whatsapp: "border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10",
    dark: "bg-white/5 border border-white/15 text-white hover:border-red-500/60 hover:text-red-400",
  };
  const cls = `${base} ${styles[variant]} ${className}`;
  if (href && href.startsWith("/")) {
    return (
      <Link to={href} onClick={onClick} className={cls} data-testid={testid}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={cls}
        data-testid={testid}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
      >
        {children}
      </a>
    );
  }
  return (
    <button type={type} onClick={onClick} className={cls} data-testid={testid}>
      {children}
    </button>
  );
};

export const Marquee = ({ items }) => (
  <div className="relative overflow-hidden border-y border-white/10 py-6" aria-hidden="true">
    <div className="marquee-track">
      {[0, 1].map((dup) => (
        <div key={dup} className="flex shrink-0 items-center">
          {items.map((item, i) => (
            <span key={i} className="flex items-center">
              <span className="px-8 font-display text-2xl font-black uppercase tracking-tight text-outline sm:text-3xl">
                {item}
              </span>
              <span className="h-2 w-2 rotate-45 bg-red-600" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const PageHero = ({ eyebrow, title, sub, img }) => (
  <section className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-24">
    {img && (
      <>
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${img})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050507]/70 via-[#050507]/85 to-[#050507]" aria-hidden="true" />
      </>
    )}
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="mt-5 max-w-4xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
          {title}
        </h1>
        {sub && <p className="mt-6 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base">{sub}</p>}
      </Reveal>
    </div>
  </section>
);
